import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addQuarters } from 'date-fns/addQuarters';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfYear } from 'date-fns/startOfYear';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { subQuarters } from 'date-fns/subQuarters';
import { subSeconds } from 'date-fns/subSeconds';
import { subWeeks } from 'date-fns/subWeeks';
import { subYears } from 'date-fns/subYears';
import type { BdTemporalUnit } from './units';
import { formatRelativeIntervalDisplay } from './relative-date-interval-display';

export interface BdIntervalRelativePart {
  temporalUnit: BdTemporalUnit;
  count: number;
}

export class BdIntervalRelative {
  public static equals(a: BdIntervalRelative, b: BdIntervalRelative): boolean {
    return (
      a.start.temporalUnit === b.start.temporalUnit &&
      a.start.count === b.start.count &&
      a.duration.temporalUnit === b.duration.temporalUnit &&
      a.duration.count === b.duration.count
    );
  }

  private _startDate: Date;
  private _endDate: Date;

  constructor(
    private _start: BdIntervalRelativePart,
    private _duration: BdIntervalRelativePart,
  ) {
    this._startDate = this._getStartDate();
    this._endDate = this._getEndDate(this._startDate, this._duration);
  }

  public toJSON() {
    return {
      start: this._start,
      duration: this._duration,
    };
  }

  public toDisplayString(referenceDate?: Date): string {
    return formatRelativeIntervalDisplay(this.toJSON(), referenceDate);
  }

  /**
   * Get the date, where the trigger should be executed the next time.
   *
   * New concept (based on https://bindoc.atlassian.net/browse/BDA-3707) produce
   * a shorter time range for the next execution date.
   *
   * Not at the end of the duration/time range, the next execution date will start.
   * Now based on the duration unit, every first of this unit will trigger the next execution date.
   */
  public getTriggerDateNextInterval(): Date {
    const nextIntervalStart = new Date();
    let triggerDate = null;

    switch (this._duration.temporalUnit) {
      case 'year':
        triggerDate = startOfYear(addYears(nextIntervalStart, 1));
        break;
      case 'quarter':
        triggerDate = startOfQuarter(addQuarters(nextIntervalStart, 1));
        break;
      case 'month':
        triggerDate = startOfMonth(addMonths(nextIntervalStart, 1));
        break;
      case 'week':
        triggerDate = startOfWeek(addWeeks(nextIntervalStart, 1));
        break;
      case 'day':
        triggerDate = addDays(nextIntervalStart, 1);
        break;
    }

    return startOfDay(triggerDate);
  }

  get startDate(): Date {
    return this._startDate;
  }

  get endDate(): Date {
    return this._endDate;
  }

  get start(): BdIntervalRelativePart {
    return this._start;
  }

  set start(value: BdIntervalRelativePart) {
    this._start = value;
    this._startDate = this._getStartDate();
    this._endDate = this._getEndDate(this._startDate, this._duration);
  }

  get duration(): BdIntervalRelativePart {
    return this._duration;
  }

  set duration(value: BdIntervalRelativePart) {
    this._duration = value;
    this._endDate = this._getEndDate(this._startDate, this._duration);
  }

  private _getStartDate(): Date {
    let date;

    switch (this._start.temporalUnit) {
      case 'year':
        date = subYears(new Date(), this._start.count);
        date = startOfYear(date);
        break;
      case 'quarter':
        date = subQuarters(new Date(), this._start.count);
        date = startOfQuarter(date);
        break;
      case 'month':
        date = subMonths(new Date(), this._start.count);
        date = startOfMonth(date);
        break;
      case 'week':
        date = subWeeks(new Date(), this._start.count);
        date = startOfWeek(date);
        break;
      case 'day':
        date = subDays(new Date(), this._start.count);
    }

    return startOfDay(date);
  }

  private _getEndDate(startDate: Date, duration: BdIntervalRelativePart): Date {
    let date;

    switch (duration.temporalUnit) {
      case 'year':
        date = addYears(startDate, duration.count);
        break;
      case 'quarter':
        date = addQuarters(startDate, duration.count);
        break;
      case 'month':
        date = addMonths(startDate, duration.count);
        break;
      case 'week':
        date = addWeeks(startDate, duration.count);
        break;
      case 'day':
        date = addDays(startDate, duration.count);
    }

    date = startOfDay(date);

    return subSeconds(date, 1);
  }
}
